import React, { useMemo, useState } from "react";
import {
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { useClaudeCode } from "../../context/ClaudeCodeContext";
import type { AppRow } from "../../utils/types";
import AppStatusBadge from "./AppStatusBadge";
import AppPreviewLink from "./AppPreviewLink";
import UserCell from "../UserCell";

const formatDateTime = (s: string | null | undefined) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return s;
  }
};

type AppRowWithSr = AppRow & { _srNo: number };

const AppAnalytics: React.FC = () => {
  const { apps, appsLoading, appsTotal, appFilters, setAppFilters } =
    useClaudeCode();

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const totalPages = Math.max(
    1,
    Math.ceil(appsTotal / appFilters.page_size)
  );

  const accessors = useMemo<
    Record<string, (a: AppRow) => string | number | null>
  >(
    () => ({
      user_email: (a) => (a.user_name || a.user_email || "").toLowerCase(),
      app_name: (a) => (a.app_name || "").toLowerCase(),
      description: (a) => (a.description || "").toLowerCase(),
      status: (a) => a.status,
      created_at: (a) => a.created_at || "",
      updated_at: (a) => a.updated_at || "",
    }),
    []
  );

  const tableData = useMemo<AppRowWithSr[]>(() => {
    const sorted = sortKey
      ? [...apps].sort((a, b) => {
          const accessor = accessors[sortKey];
          if (!accessor) return 0;
          const aVal = accessor(a);
          const bVal = accessor(b);
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          const cmp = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
          });
          return sortDir === "desc" ? -cmp : cmp;
        })
      : apps;
    return sorted.map((a, i) => ({
      ...a,
      _srNo: appFilters.page * appFilters.page_size + i + 1,
    }));
  }, [
    apps,
    sortKey,
    sortDir,
    accessors,
    appFilters.page,
    appFilters.page_size,
  ]);

  const columns = useMemo(
    (): TableColumn<AppRowWithSr>[] => [
      {
        key: "_srNo",
        header: "Sr No",
        accessor: (row) => row._srNo,
        width: "70px",
        render: (_, row) => (
          <span className="text-gray-600">{row._srNo}</span>
        ),
      },
      {
        key: "user_email",
        header: "User",
        accessor: (row) => row.user_name || row.user_email,
        sortable: true,
        width: "18%",
        render: (_, row) => (
          <UserCell
            user_email={row.user_email}
            user_name={row.user_name}
            image={row.image}
          />
        ),
      },
      {
        key: "app_name",
        header: "App Name",
        accessor: (row) => row.app_name,
        sortable: true,
        width: "16%",
        render: (_, row) => (
          <span className="font-medium text-gray-900 truncate" title={row.app_name}>
            {row.app_name}
          </span>
        ),
      },
      {
        key: "description",
        header: "Description",
        accessor: (row) => row.description || "",
        sortable: true,
        width: "30%",
        render: (_, row) => {
          const text = row.description || "—";
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-gray-600 line-clamp-2 text-sm">
                    {text}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[420px] whitespace-pre-wrap break-words">
                    {text}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        accessor: (row) => row.status,
        sortable: true,
        width: "110px",
        align: "center",
        render: (_, row) => <AppStatusBadge status={row.status} />,
      },
      {
        key: "preview_url",
        header: "Preview",
        accessor: () => "",
        width: "120px",
        align: "center",
        render: (_, row) => (
          <AppPreviewLink
            url={row.preview_deployed_url ?? row.preview_url ?? null}
            appName={row.app_name}
          />
        ),
      },
      {
        key: "created_at",
        header: "Created At",
        accessor: (row) => row.created_at,
        sortable: true,
        width: "150px",
        render: (_, row) => (
          <span className="text-gray-600 text-sm">
            {formatDateTime(row.created_at)}
          </span>
        ),
      },
      {
        key: "updated_at",
        header: "Updated At",
        accessor: (row) => row.updated_at,
        sortable: true,
        width: "150px",
        render: (_, row) => (
          <span className="text-gray-600 text-sm">
            {formatDateTime(row.updated_at)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <DataTable<AppRowWithSr>
        className="flex-1 min-h-0 overflow-auto"
        maxHeight="calc(100vh - 360px)"
        columns={columns}
        data={tableData}
        loading={appsLoading}
        emptyMessage="No apps found."
        stickyHeader
        stickyFooter
        showActions={false}
        onSort={(key, direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
        sortConfig={
          sortKey ? { key: sortKey, direction: sortDir } : undefined
        }
        pagination={{
          currentPage: appFilters.page + 1,
          totalPages,
          pageSize: appFilters.page_size,
          totalItems: appsTotal,
          onPageChange: (p) =>
            setAppFilters({ ...appFilters, page: Math.max(0, p - 1) }),
          onPageSizeChange: (size) =>
            setAppFilters({ ...appFilters, page_size: size, page: 0 }),
          pageSizeOptions: [25, 50, 100],
        }}
      />
    </div>
  );
};

export default AppAnalytics;
